import { useId, useRef, useState, useEffect } from 'react';
import { useRoving } from './useRoving';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
} from '@floating-ui/react-dom';
import mergeRefs from 'react-merge-refs';

export const useEmojiPicker = (emoji, setEmoji, isDefaultOpen) => {
  const [isOpen, setIsOpen] = useState(isDefaultOpen || false);

  const openEmojiPicker = () => setIsOpen(true);
  const closeEmojiPicker = () => setIsOpen(false);
  const toggleEmojiPicker = () => setIsOpen(!isOpen);

  const id = useId();
  const listId = 'emoji-picker-list' + id;
  const triggerId = 'emoji-picker-trigger' + id;

  const { x, y, reference, floating, strategy } = useFloating({
    middleware: [
      offset({
        mainAxis: 16,
      }),
      flip(),
      shift(),
    ],
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
  });

  const listRef = useRef();
  const triggerRef = useRef();

  useRoving(listRef, x);

  useEffect(() => {
    const currentList = listRef.current;

    if (!currentList) {
      return;
    }

    const onMouseDownHandler = (e) => {
      if (
        !currentList.contains(e.target) &&
        !triggerRef.current.contains(e.target)
      ) {
        closeEmojiPicker();
      }
    };

    document.body.addEventListener('mousedown', onMouseDownHandler);

    return () => {
      document.body.removeEventListener('mousedown', onMouseDownHandler);
    };
  });

  const listProps = (ref, props) => {
    const onKeyDownHandler = (e) => {
      if (e.key === 'Escape' && listRef.current) {
        e.stopPropagation();
        closeEmojiPicker();
        window.setTimeout(() => triggerRef.current.focus(), 0);
      }
    };

    const rps = {
      id: listId,
    };

    const kbd = {
      onKeyDown: onKeyDownHandler,
    };

    const rct = {
      ref: mergeRefs([listRef, ref, floating]),
      style: {
        position: strategy,
        top: y ?? 0,
        left: x ?? 0,
        minWidth:
          // prettier-ignore
          listRef.current &&
          (listRef.current.offsetWidth - listRef.current.clientWidth) +
          257 +
          'px',
      },
    };

    return {
      ...rps,
      ...kbd,
      ...rct,
      ...props,
    };
  };

  const iconProps = (value) => {
    const rps = {
      role: 'menuitem',
    };

    const kbd = {
      onClick: () => {
        setEmoji(value);
        closeEmojiPicker();
      },
    };

    return {
      ...rps,
      ...kbd,
    };
  };

  const triggerProps = (ref, props) => {
    const rps = {
      id: triggerId,
      'aria-haspopup': true,
      'aria-controls': listId,
      'aria-expanded': isOpen,
    };

    const kbd = {
      onClick: toggleEmojiPicker,
    };

    const rct = {
      ref: mergeRefs([triggerRef, ref, reference]),
    };

    return {
      ...rps,
      ...kbd,
      ...rct,
      emoji,
      ...props,
    };
  };

  return {
    isOpen,
    setIsOpen,
    listProps,
    iconProps,
    triggerProps,
  };
};
